import { atom, useAtomValue } from 'jotai';
import {
    cpOrderInvoiceDetailsTableFiltersAtom,
    cpOrderInvoiceDetailsTableStateAtom,
    cpOrderInvoiceDetailsTableStatsAtom,
    cpOrderInvoiceDetailsTableViewAtom,
    cpOrderInvoicesDetailsModelAtom,
    cpOrderInvoicesDetailsPageStateAtom,
} from './cp_order_invoices_details_atoms';
import { CpOrderInvoicesDataItem } from '../list/cp_order_invoices_list_types';
import { useEffect, useMemo } from 'react';
import { tableDataFilterItems } from '../../../core/table/utils/tableDataFilterItems';
import { tableDataPageItems } from '../../../core/table/utils/tableDataPageItems';
import { tableDataSortItems } from '../../../core/table/utils/tableDataSortItems';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { TableDataState } from '../../../core/table/table.types';
import { CpOrderInvoicesDetailsLineItem } from './cp_order_invoices_details_types';
import {
    cpOrderInvoiceLineItemsAllColumnsView,
    CpOrderInvoiceLineItemsColumnSpec,
    cpOrderInvoiceLineItemsWithOptionalColumnsView,
} from './CpOrderInvoiceLineItemsTableSpec';
import { tblSpecLoadView } from '../../../core/table/tableViewsRepo';

const LineItemData = atom<TableDataState<CpOrderInvoicesDetailsLineItem>>({ items: [], dataItems: [] });

type UseInvoiceDetailsDataReturn = {
    items: CpOrderInvoicesDetailsLineItem[];
};

const defaultView = cpOrderInvoiceLineItemsAllColumnsView();
const colSpec = CpOrderInvoiceLineItemsColumnSpec();

export const useInvoiceLineItemsData = (): UseInvoiceDetailsDataReturn => {
    const [view, setView] = useImmerAtom(cpOrderInvoiceDetailsTableViewAtom);
    const [filters] = useImmerAtom(cpOrderInvoiceDetailsTableFiltersAtom);
    const [stats, setStats] = useImmerAtom(cpOrderInvoiceDetailsTableStatsAtom);
    const setTableState = useSetImmerAtom(cpOrderInvoiceDetailsTableStateAtom);
    const [data, setData] = useImmerAtom(LineItemData);
    const detailsPageState = useAtomValue(cpOrderInvoicesDetailsPageStateAtom);

    const invoice = useInvoiceDetailsData();

    const filtered = useMemo(() => {
        return tableDataFilterItems<CpOrderInvoicesDetailsLineItem>(data.dataItems, view.cols, filters);
    }, [data.dataItems, view.cols, filters]);

    const sorted = tableDataSortItems<CpOrderInvoicesDetailsLineItem>(filtered, view.cols, view.sort);
    const paged = tableDataPageItems<CpOrderInvoicesDetailsLineItem>(sorted, stats.paging.size, stats.paging.page);

    useEffect(() => {
        // Use pre-computed feature flag from detail page state atom
        const hasTrueUpFeature = detailsPageState.hasTrueUpFeature;
        const showOverallocated = !detailsPageState.isVeriSaveOnly;
        let canTrueUp = false;
        let showRebates = false;
        invoice.Items.forEach(item => {
            if (!canTrueUp && !!item.Overallocated && hasTrueUpFeature) {
                canTrueUp = true;
            }
            if (!showRebates && !!item.IsRebate) {
                showRebates = true;
            }
        });
        const reconcileView = cpOrderInvoiceLineItemsWithOptionalColumnsView(canTrueUp, showRebates, showOverallocated);
        const views = [defaultView, reconcileView];

        const currentViewId = canTrueUp || showRebates || !showOverallocated ? reconcileView.key : defaultView.key;
        setTableState(draft => {
            draft.views = views;
            draft.currentViewId = currentViewId;

            const currentView = tblSpecLoadView(
                currentViewId,
                views,
                colSpec,
                canTrueUp
                    ? cpOrderInvoiceLineItemsAllColumnsView
                    : (): any =>
                          cpOrderInvoiceLineItemsWithOptionalColumnsView(canTrueUp, showRebates, showOverallocated),
            );

            setView(() => currentView);
        });

        setData(() => ({ items: invoice.Items, dataItems: invoice.Items }));
    }, [invoice, setData, setTableState, setView, detailsPageState.hasTrueUpFeature, detailsPageState.isVeriSaveOnly]);

    useEffect(() => {
        setStats(draft => {
            draft.fltStats.filtered = filtered.length;
            draft.fltStats.total = data.dataItems.length;
            draft.paging.total = Math.ceil(filtered.length / draft.paging.size);
        });
    }, [filtered.length, data.dataItems.length, setStats, stats.paging.size]);

    return {
        items: paged,
    };
};

export const useInvoiceDetailsData = (): CpOrderInvoicesDataItem => {
    // Returns the current value of the invoice details model atom
    const invoice = useAtomValue(cpOrderInvoicesDetailsModelAtom);
    return invoice;
};
